import { appsApi, errMsg } from "@/lib/appsApi";

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const RETRY_DELAYS = [1000, 3000, 8000];
const WINDOW_MS = 15000;

// Tracks bytes sent over a rolling window so speed reflects the current connection, not the whole run.
const makeMeter = (totalBytes, onProgress) => {
  const samples = [{ t: Date.now(), b: 0 }];
  let lastEmit = 0;
  return (sent, force = false) => {
    const now = Date.now();
    if (!force && now - lastEmit < 500) return;
    lastEmit = now;
    samples.push({ t: now, b: sent });
    while (samples.length > 2 && now - samples[0].t > WINDOW_MS) samples.shift();
    const dt = (now - samples[0].t) / 1000;
    const speed = dt >= 1 ? (sent - samples[0].b) / dt : null;
    const eta = speed > 0 ? Math.max(0, (totalBytes - sent) / speed) : null;
    onProgress(Math.min(100, Math.round((sent / totalBytes) * 100)), { sent, total: totalBytes, speed, eta });
  };
};

export const uploadInChunks = async (file, onProgress, signal, onStart = () => {}) => {
  let start;
  try {
    start = await appsApi.post("/uploads", { filename: file.name, size: file.size }, { signal });
  } catch (err) {
    throw new Error(errMsg(err, "Starting the upload"));
  }
  const { upload_id: id, chunk_size: size, total_chunks: total } = start?.data ?? {};
  onStart(id);
  const meter = makeMeter(file.size, onProgress);
  for (let i = 0; i < total; i += 1) {
    const blob = file.slice(i * size, Math.min(file.size, (i + 1) * size));
    const base = i * size;
    for (let attempt = 0; ; attempt += 1) {
      try {
        await appsApi.put(`/uploads/${id}/chunks/${i}`, blob, {
          headers: { "Content-Type": "application/octet-stream" }, signal, timeout: 120000,
          onUploadProgress: (e) => meter(base + Math.min(e?.loaded ?? 0, blob.size)),
        });
        break;
      } catch (err) {
        if (signal?.aborted) throw new Error("Upload cancelled.");
        const status = err?.response?.status;
        if (attempt >= RETRY_DELAYS.length || [401, 403, 404, 409].includes(status)) {
          throw new Error(errMsg(err, `Uploading part ${i + 1} of ${total} (after ${attempt + 1} attempt${attempt ? "s" : ""})`));
        }
        await sleep(RETRY_DELAYS[attempt]);
      }
    }
    meter(base + blob.size, true);
  }
  return id;
};
